<template>
	<v-expansion-panels v-model="panel" flat accordion>
		<ExpansionPanel :title="'Basics'">
			<SettingSwitch
				:title="'Label'"
				:placeholder="'Type your title here'"
				:switchValue.sync="titleEnabled"
				:textFieldValue.sync="titleValue"
			/>
			<SwitchOption option-name="Required" :switch-model.sync="requiredEnabled" />
		</ExpansionPanel>
		<ExpansionPanel :title="'Options'">
			<AppTabSelector :tabItems="tabItems" v-model="mode" />
			<div class="tab-option pt-2 mb-6">
				<DropdownManualOptionsSettings
					:noSuffix="optionValueOption"
					v-if="isManualMode"
					:localField="localField"
					@updateLocalField="({ key, value }) => setLocalFieldKey(key, value)"
				/>
				<MultipleSelectTableSourceSettingsEditor
					v-if="isTableMode"
					:localField="localField"
					@updateLocalField="({ key, value }) => setLocalFieldKey(key, value)"
				/>
			</div>
			<SwitchOption option-name="Option-Value mode" :switch-model.sync="optionValueOption" />
		</ExpansionPanel>
		<!-- <ExpansionPanel :title="'Controls'">
			<SwitchOption option-name="Autocomplete mode" :switch-model.sync="autocompleteEnabled" />
		</ExpansionPanel> -->
		<ExpansionPanel :title="'Explanations'">
			<SettingSwitch
				:title="'Description'"
				:placeholder="'Type your description here'"
				:switchValue.sync="descriptionEnabled"
				:textFieldValue.sync="descriptionValue"
			/>
			<SettingSwitch
				:title="'Placeholder'"
				:placeholder="'Type placeholder text'"
				:switchValue.sync="placeholderEnabled"
				:textFieldValue.sync="placeholderValue"
			/>
		</ExpansionPanel>
	</v-expansion-panels>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SwitchOption from '../FieldOptions/SwitchOption';
import SettingSwitch from '../WidgetSettings/SettingSwitch';
import ExpansionPanel from '../WidgetSettings/ExpansionPanel';
import WidgetFormService from '@/components/WidgetForm/WidgetFormService';
import DropdownManualOptionsSettings from './DropdownManualOptionsSettings';
import { getPreviousStepDataForNodeByUUID } from '@/store/modules/nodes/getters';
import { replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import FieldSettingsMixin from '@/components/BlockEditorWidget/FieldSettings/FieldSettingsMixin';
import TableActionEditorService from '../../../BlockEditor/TableActionEditor/table-action-edtior-service';
import MultipleSelectTableSourceSettingsEditor from '../FieldOptions/MultipleSelectTableSourceSettingsEditor';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

export default {
	name: 'DropdownFieldSettings',
	mixins: [FieldSettingsMixin],
	components: {
		SwitchOption,
		SettingSwitch,
		ExpansionPanel,
		MultipleSelectTableSourceSettingsEditor,
		DropdownManualOptionsSettings,
	},
	methods: {
		...mapActions(['selectedNode/resetWidgetFieldTestStatus']),
		translatePreviousStepsValue(value) {
			return this.previousSteps
				? WidgetFormService.processPreviousSteps(
						value,
						this.previousSteps,
						this.replaceInsertDataStringDict,
						this.configurationsMappingBlocksUUIDs
				  )
				: value;
		},
	},
	computed: {
		...mapGetters([
			getPreviousStepDataForNodeByUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		dropdownOptions: {
			get() {
				if (this.isManualMode) {
					return (this.localField?.metadata.options.dropdown_options || []).map((o) => ({
						...o,
						text: this.translatePreviousStepsValue(o.text),
					}));
				}
				if (this.isTableMode) {
					// for pre select
					const { text_column: text, value_column: value } = this.localField?.metadata.options.table_mode;
					const options = this.tableSourceRows
						.filter((row) => text in row && (value ? value in row : true))
						.map((row, index) => ({
							id: `${index}`,
							text: row[text],
							value: value ? row[value] : '',
						}));
					return options;
				}
				return [];
			},
			set(value) {
				// only manual sets here
				this.setLocalFieldKey('metadata.options.dropdown_options', value);
			},
		},
		preselectOptions() {
			return this.optionValueOption ? this.dropdownOptions : this.dropdownOptions.map((o) => o.text);
		},
		preselectOption: {
			set(value) {
				this.setLocalFieldKey('metadata.options.preselect', value);
			},
			get() {
				return this.localField?.metadata.options.preselect;
			},
		},
		optionValueOption: {
			set(value) {
				if (value) {
					this.userCustomOptions = false;
				}
				this.setLocalFieldKey('metadata.options.option_value.enabled', value);
			},
			get() {
				return this.localField?.metadata.options.option_value?.enabled;
			},
		},
		userCustomOptions: {
			set(value) {
				if (!this.localField?.metadata.options.user_custom_options) {
					this.setLocalFieldKey('metadata.options.user_custom_options', { enabled: value });
				} else {
					this.setLocalFieldKey('metadata.options.user_custom_options.enabled', value);
				}
			},
			get() {
				return this.localField?.metadata.options.user_custom_options?.enabled;
			},
		},
		preselectOptionValue: {
			get() {
				return this.preselectOption.value;
			},
			set(value) {
				this.preselectOption = Object.assign({}, { ...this.preselectOption }, { value });
			},
		},
		mode: {
			get() {
				return this.localField?.metadata.options.dropdown_mode?.value || 0;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.dropdown_mode.value', value);
			},
		},
		isManualMode() {
			return this.mode === 0;
		},
		isTableMode() {
			return this.mode === 1;
		},
		tableSource() {
			return this.localField?.metadata.options.previous_steps.table_mode_source.value;
		},
		tableSourceRows() {
			return (
				TableActionEditorService.rowsFromPreviousSteps(
					this.tableSource,
					this[getPreviousStepDataForNodeByUUID.fullPath],
					this[replaceInsertDataStringDictionary.fullPath],
					this[configurationsMappingBlocksUUIDs.fullPath]
				) || []
			);
		},
		allowCustomUserOptions() {
			return !this.optionValueOption;
		},
		resetNeededReleventObject() {
			let res = {};
			if (this.localField?.metadata?.options) {
				this.resetNeededReleventFields.map((label) => (res = { ...res, [label]: this.localField?.metadata.options[label] }));
			}
			return res;
		},
		fieldOptions() {
			return this.localField?.metadata?.options;
		},
		tableMode() {
			return this.localField?.metadata?.options.table_mode;
		},
		optionValue() {
			return this.localField?.metadata?.options.option_value;
		},
		dropdownMode() {
			return this.localField?.metadata?.options.dropdown_mode;
		},
		userSelection() {
			return this.localField?.metadata?.options.user_selection;
		},
		dropdownOptionsArr() {
			return this.localField?.metadata?.options.dropdown_options;
		},
		userSelectionMultiple: {
			get() {
				return this.localField.metadata.options.user_selection.multiple;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.multiple', value);
			},
		},
		autocompleteEnabled: {
			get() {
				return this.localField.metadata.options.autocomplete.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.autocomplete.enabled', value);
			},
		},
	},
	data: () => ({
		tabItems: [
			{ tab: 0, title: 'Manual' },
			{ tab: 1, title: 'Table' },
		],
	}),
	watch: {
		tableMode: {
			handler(newV, oldV) {
				if (oldV !== undefined) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},
		optionValue: {
			handler(newV, oldV) {
				if (oldV !== undefined) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},
		dropdownMode: {
			handler(newV, oldV) {
				if (oldV !== undefined) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},

		userSelection: {
			handler(newV, oldV) {
				if (oldV !== undefined) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},
		dropdownOptionsArr: {
			handler(newV, oldV) {
				if (oldV !== undefined && oldV?.length !== newV?.length) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},
		userCustomOptions: {
			handler(newV, oldV) {
				if (oldV !== undefined) {
					this['selectedNode/resetWidgetFieldTestStatus'](this.localField?.id);
				}
			},
		},
	},
};
</script>

<style scoped lang="scss">
.block-editor-widget-settings-text-input {
	.field-setting-section {
		.dropdown-options-section {
			max-height: 178px;
		}
	}

	.v-select-container:not(.is-selectable) {
		pointer-events: none;
		opacity: 0.6;
		transition: 0.3;
	}
}
</style>
